import React, {useState, useRef, useEffect} from 'react';
import {
  InlineDatumReactComponentProps,
  useAnalytics,
} from '@stripe-internal/inline-data';
import {defineMessages, useIntl} from 'react-intl';
import {AnimatePresence, animate} from '@sail/engine';
import {view} from '@sail/ui';
import {TextRow} from '@stripe-internal/conversations-ui';
import {
  idealInquiryFormViewed,
  idealInquiryFormSearchSubmitted,
  idealInquiryFormSubmitted,
  idealInquiryFormSubmitError,
  idealInquiryFormSearchError,
} from './analytics';
import {SearchPaymentForm, SubmitEvidenceForm} from './FormStep';
import {SearchFormData} from './FormStep/SearchPaymentForm';

interface EvidenceFormData {
  email: string;
  message: string;
}

type Step = 'search' | 'evidence';

export type ValueProps = {
  conversation_id?: string;
  amount?: number;
  payment_date?: string;
  iban?: string;
  email?: string;
  message?: string;
  payment_found?: boolean;
  submitted?: boolean;
  error?: string;
  step?: Step;
  hcaptcha_sitekey?: string;
  pi_token?: string;
};

type CommonPayload = {
  amount: number;
  payment_date: string;
  iban: string;
  hcaptcha_token: string;
  hcaptcha_ekey: string;
  origin: string;
  pi_token?: string;
  step: Step;
};

type SearchPaymentPayload = CommonPayload;

type SubmitEvidencePayload = CommonPayload & {
  email: string;
  message: string;
};

export const messages = defineMessages({
  paymentFoundChat: {
    id: 'ideal_inquiry.payment_found_chat',
    defaultMessage:
      'Payment located, please share your contact information to complete your inquiry.',
  },
  paymentNotFound: {
    id: 'ideal_inquiry.payment_not_found',
    defaultMessage:
      'Payment not found. Please check your information and try again.',
  },
  inquiryAlreadySubmittedError: {
    id: 'ideal_inquiry.inquiry_already_submitted_error',
    defaultMessage:
      'An inquiry has already been submitted for this payment. We are actively investigating this matter.',
  },
  genericError: {
    id: 'ideal_inquiry.generic_error',
    defaultMessage:
      'There was an issue with your inquiry. Please try again later.',
  },
});

export const IdealInquiryForm = (props: InlineDatumReactComponentProps) => {
  const {datum, action, onDone, isDone} = props;
  const intl = useIntl();
  const analytics = useAnalytics();
  const loggedViewed = useRef<boolean>(false);

  const {
    conversation_id: conversationId,
    amount,
    payment_date: paymentDate,
    iban,
    email,
    message,
    payment_found: paymentFound,
    submitted,
    error,
    step,
    hcaptcha_sitekey: hcaptchaSitekey,
    pi_token: piToken,
  } = datum.value as ValueProps;

  const initialSearchFormData: SearchFormData = {
    amount: amount ?? null,
    payment_date: paymentDate ?? '',
    iban: iban ?? '',
    hcaptcha_token: null,
    hcaptcha_ekey: null,
    origin: '',
  };

  const initialEvidenceFormData: EvidenceFormData = {
    email: email ?? '',
    message: message ?? '',
  };

  const [searchFormData, setSearchFormData] = useState<SearchFormData>(
    initialSearchFormData,
  );
  const [evidenceFormData, setEvidenceFormData] = useState<EvidenceFormData>(
    initialEvidenceFormData,
  );

  const searchFormDataRef = useRef(searchFormData);
  const evidenceFormDataRef = useRef(evidenceFormData);

  const [showForm, setShowForm] = useState<boolean>(true);

  useEffect(() => {
    searchFormDataRef.current = searchFormData;
  }, [searchFormData]);

  useEffect(() => {
    evidenceFormDataRef.current = evidenceFormData;
  }, [evidenceFormData]);

  // Track form view
  useEffect(() => {
    if (loggedViewed.current || isDone) {
      return;
    }
    loggedViewed.current = true;
    analytics.viewed(idealInquiryFormViewed, {
      conversation_id: conversationId,
    });
  }, [analytics, conversationId, isDone]);

  const [pending, setPending] = useState<boolean>(false);
  const [currentStep, setCurrentStep] = useState<Step>(step ?? 'search');
  const [searchFormFrozen, setSearchFormFrozen] = useState<boolean | undefined>(
    isDone,
  );
  const [evidenceFormFrozen, setEvidenceFormFrozen] = useState<
    boolean | undefined
  >(isDone);
  // Removed evidenceFormErrors state as it's now handled in SubmitEvidenceForm
  const [submissionError, setSubmissionError] = useState<string | null>(null);

  // Update current step when prop changes
  useEffect(() => {
    if (step) {
      setCurrentStep(step);
    }
  }, [step]);

  // Reset error state when user interacts with form
  const clearErrorOnInteraction = () => {
    if (submissionError) {
      setSubmissionError(null);
    }
  };

  // Handle backend responses
  useEffect(() => {
    if (error) {
      setPending(false);
      setSearchFormFrozen(false);
      setEvidenceFormFrozen(false);

      if (error === 'payment_not_found') {
        analytics.action(idealInquiryFormSearchError, {
          error_type: 'payment_not_found',
          conversation_id: conversationId,
        });
        setSubmissionError(intl.formatMessage(messages.paymentNotFound));
      } else if (error === 'inquiry_already_submitted') {
        analytics.action(idealInquiryFormSubmitError, {
          error_type: 'inquiry_already_submitted',
          conversation_id: conversationId,
        });
        setSubmissionError(
          intl.formatMessage(messages.inquiryAlreadySubmittedError),
        );
      } else {
        analytics.action(idealInquiryFormSubmitError, {
          error_type: error || 'unknown',
          conversation_id: conversationId,
        });
        setSubmissionError(intl.formatMessage(messages.genericError));
      }
      return;
    }

    if (paymentFound === true && currentStep === 'search') {
      setPending(false);
      setSearchFormFrozen(false);
      // Keep details form unfrozen for input
      setEvidenceFormFrozen(false);
      setCurrentStep('evidence');
      analytics.action(idealInquiryFormSearchSubmitted, {
        outcome: 'payment_found',
        conversation_id: conversationId,
      });
      return;
    }

    if (submitted === true && !isDone) {
      setPending(false);
      analytics.action(idealInquiryFormSubmitted, {
        outcome: 'success',
        conversation_id: conversationId,
      });
      onDone({
        type: 'success',
        message: 'IdealInquiryForm',
        extra: {
          searchData: {...searchFormDataRef.current},
          detailsData: {...evidenceFormDataRef.current},
          submitted,
          payment_found: paymentFound,
          outcome: 'submitted',
        },
      });
      setShowForm(false);
    }
  }, [
    isDone,
    onDone,
    error,
    submitted,
    paymentFound,
    currentStep,
    intl,
    analytics,
    conversationId,
  ]);

  function searchPaymentForm() {
    const handleSearchPaymentSubmit = (formData: SearchFormData) => {
      if (pending) return;

      setPending(true);
      setSearchFormFrozen(true);

      // Update searchFormData state to keep it in sync
      setSearchFormData(formData);

      const payload: SearchPaymentPayload = {
        amount: formData.amount!,
        payment_date: formData.payment_date,
        iban: formData.iban,
        hcaptcha_token: formData.hcaptcha_token!,
        hcaptcha_ekey: formData.hcaptcha_ekey!,
        origin,
        pi_token: undefined,
        step: 'search',
      };

      analytics.action(idealInquiryFormSearchSubmitted, {
        conversation_id: conversationId,
      });

      action(payload);
    };

    return showForm ? (
      <SearchPaymentForm
        initialAmount={searchFormData.amount}
        initialPaymentDate={searchFormData.payment_date}
        initialIban={searchFormData.iban}
        handleSubmit={handleSearchPaymentSubmit}
        isPending={pending}
        isDisabled={searchFormFrozen}
        submissionError={submissionError}
        clearErrorOnInteraction={clearErrorOnInteraction}
        hcaptchaSitekey={hcaptchaSitekey}
        isFormComplete={currentStep === 'evidence'}
      />
    ) : null;
  }

  function submitEvidenceForm() {
    const handleSubmitEvidenceForm = (formData: EvidenceFormData) => {
      if (pending) return;

      setPending(true);
      setEvidenceFormFrozen(true);

      setEvidenceFormData(formData);

      const payload: SubmitEvidencePayload = {
        amount: searchFormData.amount!,
        payment_date: searchFormData.payment_date,
        iban: searchFormData.iban,
        hcaptcha_token: searchFormData.hcaptcha_token!,
        hcaptcha_ekey: searchFormData.hcaptcha_ekey!,
        email: formData.email,
        message: formData.message,
        origin,
        pi_token: piToken!,
        step: 'evidence',
      };

      analytics.action(idealInquiryFormSubmitted, {
        conversation_id: conversationId,
      });
      action(payload);
    };

    return showForm && currentStep === 'evidence' ? (
      <SubmitEvidenceForm
        initialEmail={evidenceFormData.email}
        initialMessage={evidenceFormData.message}
        handleSubmit={handleSubmitEvidenceForm}
        isPending={pending}
        isDisabled={evidenceFormFrozen}
        submissionError={submissionError}
        clearErrorOnInteraction={clearErrorOnInteraction}
      />
    ) : null;
  }

  function paymentFoundChat() {
    return isDone === true || currentStep === 'evidence' ? (
      <TextRow
        body={intl.formatMessage(messages.paymentFoundChat)}
        // eslint-disable-next-line react/style-prop-object
        style="none"
      />
    ) : null;
  }

  return (
    <AnimatePresence>
      <view.div
        css={{stack: 'y', alignX: 'stretch', gap: 'small'}}
        uses={[
          animate.out({
            duration: 300,
            opacity: 0,
            easing: 'cubic-bezier(0.16, 1, 0.3, 1)',
          }),
        ]}
      >
        {searchPaymentForm()}
        {paymentFoundChat()}
        {submitEvidenceForm()}
      </view.div>
    </AnimatePresence>
  );
};
